/**
 * Zod schemas for F-conn connection types.
 *
 * Key constraints:
 *  - The sfdx auth URL allows an empty clientSecret segment (default CLI app).
 *  - `Connection` is the only shape the server returns to clients; the
 *    `createConnectionRequest` schema validates inbound create bodies.
 */
import { z } from 'zod';
import { ENV_CLASSES } from '../types/project.js';
// ── Auth URL ──────────────────────────────────────────────────────────────────
/**
 * Validates a Salesforce sfdx auth URL.
 *
 * Format: `force://<clientId>:<clientSecret>:<refreshToken>@<instanceHost>`
 *
 * Rules:
 *  - Must start with `force://`
 *  - clientId    — non-empty, no `:` or `@`
 *  - clientSecret — MAY be empty (default CLI connected app uses `PlatformCLI::`)
 *  - refreshToken — non-empty, no `@`
 *  - instanceHost — non-empty, no spaces
 */
export const AUTH_URL_REGEX = /^force:\/\/([^:@]+):([^:@]*):([^@\s]+)@([^\s@]+)$/;
export const sfAuthUrlSchema = z
    .string()
    .regex(AUTH_URL_REGEX, {
    message: 'Invalid sfdx auth URL — expected force://<clientId>:<secret>:<refreshToken>@<host>',
});
/** Parse an sfdx auth URL into its component parts. Returns null on failure. */
export function parseSfAuthUrl(url) {
    const match = AUTH_URL_REGEX.exec(url);
    if (!match)
        return null;
    const [, clientId, clientSecret, refreshToken, instanceHost] = match;
    return {
        clientId: clientId,
        clientSecret: clientSecret ?? '',
        refreshToken: refreshToken,
        instanceUrl: `https://${instanceHost}`,
    };
}
// ── Jira identifiers ──────────────────────────────────────────────────────────
/** A Jira project key: uppercase letters/digits, must start with a letter (e.g. "KAN"). */
export const JIRA_PROJECT_KEY_REGEX = /^[A-Z][A-Z0-9]{1,9}$/;
export const jiraProjectKeySchema = z.string().regex(JIRA_PROJECT_KEY_REGEX, {
    message: 'Invalid Jira project key — expected 2–10 uppercase alphanumerics starting with a letter, e.g. "KAN"',
});
/** A Jira issue key: `<PROJECT>-<number>` (e.g. "KAN-12"). */
export const JIRA_ISSUE_KEY_REGEX = /^[A-Z][A-Z0-9]{1,9}-[1-9][0-9]{0,9}$/;
export const jiraIssueKeySchema = z.string().regex(JIRA_ISSUE_KEY_REGEX, {
    message: 'Invalid Jira issue key — expected <PROJECT>-<number>, e.g. "KAN-12"',
});
/** Extract the project key from an issue key. Returns null if malformed. */
export function jiraProjectKeyOf(issueKey) {
    if (!JIRA_ISSUE_KEY_REGEX.test(issueKey))
        return null;
    return issueKey.slice(0, issueKey.lastIndexOf('-'));
}
// ── Connection kinds / methods ────────────────────────────────────────────────
export const connectionKindSchema = z.enum(['salesforce', 'git', 'jira']);
export const connectionMethodSchema = z.enum(['authurl', 'pat', 'oauth', 'apitoken']);
export const connectionHealthSchema = z.enum(['healthy', 'expired', 'error', 'unchecked']);
// ── Connection (response shape — no secrets) ──────────────────────────────────
export const connectionSchema = z.object({
    id: z.string().uuid(),
    kind: connectionKindSchema,
    method: connectionMethodSchema,
    label: z.string().min(1).max(100),
    instanceUrl: z.string().url().optional(),
    repoUrl: z.string().url().optional(),
    baseUrl: z.string().url().optional(),
    projectKey: jiraProjectKeySchema.optional(),
    username: z.string().optional(),
    health: connectionHealthSchema,
    lastCheckedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    deployBranch: z.string().min(1).max(200).optional(),
    isArmSource: z.boolean().optional(),
});
// ── Update payload (FX3 — per-org deploy branch) ──────────────────────────────
export const updateConnectionSchema = z.object({
    deployBranch: z.string().min(1).max(200).optional(),
    /** Display-only rename. The connection ID is unchanged, so pipeline wiring / saved sets / the
     *  deploy branch are untouched. */
    label: z.string().trim().min(1).max(120).optional(),
    /** Phase 6 — designate (or clear) this org as the ARM key-generation source. Setting `true`
     *  clears the flag on any other connection (single-source, enforced in the service). */
    isArmSource: z.boolean().optional(),
    /**
     * Which environment class this org belongs to — the unit deploy permission is granted against.
     *
     * Set by a project admin, and worth getting right: it decides who may release into this org.
     * Reclassifying an org from `development` to `production` immediately tightens who can deploy to
     * it, which is the intended direction; the reverse loosens it, so the UI should confirm.
     */
    envClass: z.enum(ENV_CLASSES).optional(),
});
/**
 * Replace a connection's stored secret, keeping its id.
 *
 * Separate from `updateConnectionSchema` on purpose. Rotation is not another editable attribute: it takes
 * a secret rather than a display value, it is the one update that can break every pipeline pointing at
 * the connection if it goes wrong, and it should be greppable on its own in an audit.
 *
 * It exists because there was NO way to do this. Creating a connection mints a fresh id, so re-adding a
 * repo with a new token produced a DIFFERENT connection while every pipeline stage, saved set and
 * promotion record still referenced the old one — turning a routine token rotation into a migration.
 * The id is what the rest of the system holds on to, so rotation has to preserve it.
 */
export const rotateCredentialSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('git'), token: z.string().min(1) }),
    z.object({ kind: z.literal('jira'), apiToken: z.string().min(1), email: z.string().email().optional() }),
    z.object({ kind: z.literal('salesforce'), authUrl: sfAuthUrlSchema }),
]);
// ── Create payloads ───────────────────────────────────────────────────────────
export const createSalesforceAuthUrlSchema = z.object({
    kind: z.literal('salesforce'),
    method: z.literal('authurl'),
    label: z.string().min(1).max(100),
    authUrl: sfAuthUrlSchema,
});
export const createGitPatSchema = z.object({
    kind: z.literal('git'),
    method: z.literal('pat'),
    label: z.string().min(1).max(100),
    repoUrl: z.string().url(),
    token: z.string().min(1),
});
/**
 * Jira Cloud API-token connection (brief §4.1).
 * `baseUrl` is normalised to an origin (no trailing path) by the service before storage.
 */
export const createJiraApiTokenSchema = z.object({
    kind: z.literal('jira'),
    method: z.literal('apitoken'),
    label: z.string().min(1).max(100),
    baseUrl: z.string().url(),
    email: z.string().email(),
    apiToken: z.string().min(1),
    projectKey: jiraProjectKeySchema.optional(),
});
export const createConnectionRequestSchema = z.discriminatedUnion('kind', [
    createSalesforceAuthUrlSchema,
    createGitPatSchema,
    createJiraApiTokenSchema,
]);
//# sourceMappingURL=connection.js.map